import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import workflowApi from '../../features/workflow/services/workflowApi';

export const fetchWorkflows = createAsyncThunk('workflowPipeline/fetchWorkflows', async (projectId) => {
  const res = await workflowApi.getWorkflows(projectId);
  return res?.data || res;
});

export const createWorkflow = createAsyncThunk('workflowPipeline/createWorkflow', async (data) => {
  const res = await workflowApi.createWorkflow(data);
  return res?.data || res;
});

export const deleteWorkflow = createAsyncThunk('workflowPipeline/deleteWorkflow', async (id) => {
  await workflowApi.deleteWorkflow(id);
  return id;
});

export const updateStageStatus = createAsyncThunk('workflowPipeline/updateStageStatus', async ({ workflowId, stageType, status, reviewComment }) => {
  const body = { status };
  if (reviewComment) body.reviewComment = reviewComment;
  const result = await workflowApi.updateStage(workflowId, stageType, body);
  // Backend may route COMPLETE → PENDING_APPROVAL for delegates
  const actualStatus = result?.status || status;

  // When a stage completes, the backend may auto-create projects/collections/mock servers.
  // Clear the cached projects so the sidebar picks up the new data on next load.
  if (actualStatus === 'COMPLETE') {
    sessionStorage.removeItem('projects');
  }

  return { workflowId, stageType, status: actualStatus };
});

export const assignStageUser = createAsyncThunk('workflowPipeline/assignStageUser', async ({ workflowId, stageType, userId }) => {
  await workflowApi.updateStage(workflowId, stageType, { assignedTo: userId });
  return { workflowId, stageType, userId };
});

export const generateArtifact = createAsyncThunk('workflowPipeline/generateArtifact', async ({ workflowId, stageType, prompt }) => {
  const result = await workflowApi.generateArtifact(workflowId, stageType, prompt);
  return { workflowId, stageType, artifact: result.artifact };
});

export const fetchNotifications = createAsyncThunk('workflowPipeline/fetchNotifications', async () => {
  return await workflowApi.getNotifications();
});

export const fetchNotificationCount = createAsyncThunk('workflowPipeline/fetchNotificationCount', async () => {
  return await workflowApi.getNotificationCount();
});

export const markNotificationRead = createAsyncThunk('workflowPipeline/markNotificationRead', async (id) => {
  await workflowApi.markNotificationRead(id);
  return id;
});

export const markAllNotificationsRead = createAsyncThunk('workflowPipeline/markAllNotificationsRead', async () => {
  await workflowApi.markAllNotificationsRead();
});

const workflowPipelineSlice = createSlice({
  name: 'workflowPipeline',
  initialState: {
    workflows: [],
    activeWorkflow: null,
    loading: false,
    error: null,
    notifications: [],
    unreadCount: 0,
  },
  reducers: {
    setActiveWorkflow: (state, action) => { state.activeWorkflow = action.payload; },
    clearWorkflows: (state) => { state.workflows = []; state.activeWorkflow = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkflows.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchWorkflows.fulfilled, (state, action) => { state.loading = false; state.workflows = action.payload; })
      .addCase(fetchWorkflows.rejected, (state, action) => { state.loading = false; state.error = action.error.message; })
      .addCase(deleteWorkflow.fulfilled, (state, action) => {
        state.workflows = state.workflows.filter((w) => w.id !== action.payload);
      })
      .addCase(createWorkflow.fulfilled, (state, action) => { state.workflows.push(action.payload); })
      .addCase(updateStageStatus.fulfilled, (state, action) => {
        const { workflowId, stageType, status } = action.payload;
        const wf = state.workflows.find(w => w.id === workflowId);
        if (wf) {
          const stage = wf.stages.find(s => s.type === stageType);
          if (stage) {
            stage.status = status;
            if (status === 'COMPLETE') {
              stage.completedAt = new Date().toISOString();
              // Mirror the backend's auto-advance, which hops over SKIPPED
              // stages to the next PENDING one.
              for (let i = wf.stages.indexOf(stage) + 1; i < wf.stages.length; i++) {
                if (wf.stages[i].status === 'SKIPPED') continue;
                if (wf.stages[i].status === 'PENDING') {
                  wf.stages[i].status = 'IN_PROGRESS';
                  wf.stages[i].startedAt = new Date().toISOString();
                }
                break;
              }
            }
          }
        }
        if (state.activeWorkflow?.id === workflowId) {
          state.activeWorkflow = wf;
        }
      })
      .addCase(assignStageUser.fulfilled, (state, action) => {
        const { workflowId, stageType, userId } = action.payload;
        const wf = state.workflows.find(w => w.id === workflowId);
        if (wf) {
          const stage = wf.stages.find(s => s.type === stageType);
          if (stage) stage.assignedTo = userId;
        }
      })
      .addCase(generateArtifact.fulfilled, (state, action) => {
        const { workflowId, stageType, artifact } = action.payload;
        const wf = state.workflows.find(w => w.id === workflowId);
        if (wf) {
          const stage = wf.stages.find(s => s.type === stageType);
          if (stage && artifact) {
            if (!stage.artifacts) stage.artifacts = [];
            stage.artifacts.unshift(artifact);
          }
        }
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.notifications = action.payload?.notifications || [];
      })
      .addCase(fetchNotificationCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload?.unreadCount || 0;
      })
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const n = state.notifications.find(n => n.id === action.payload);
        if (n) n.isRead = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      })
      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        state.notifications.forEach(n => n.isRead = true);
        state.unreadCount = 0;
      });
  },
});

export const { setActiveWorkflow, clearWorkflows } = workflowPipelineSlice.actions;
export default workflowPipelineSlice.reducer;
